/**
 * Round numbers for a value axis (CT-5641).
 *
 * The charts used to scale against the data's own peak, which puts the tallest
 * mark flush against the top of the plot. That is fine without an axis and
 * wrong with one: the top gridline would be labelled 142, the next 106.5, and
 * nobody reads a chart in units of 35.5.
 *
 * So the ceiling is raised to the next round number the axis can be divided
 * into. The tallest bar no longer touches the top — which is also how a reader
 * can tell the chart is not clipped.
 *
 * The ladder is denser than the usual 1/2/5: at four rows, `1/2/5` turns a
 * peak of 142 into a ceiling of 200 and throws away a quarter of the plot.
 * With 4 in the ladder it lands on 160.
 */
const MANTISSAS = Object.freeze([1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]);

/**
 * A ceiling and its ticks for a plot drawn with `rows` gridlines above the
 * baseline — the same `rows` `ChartGrid` draws, so every label sits on a line.
 *
 * @param {number} peak The largest value to fit (a stack's total, for a stack).
 * @param {number} [rows=4]
 * @returns {{ max: number, step: number, ticks: number[] }} `ticks` ascending
 *   from 0; `max` is what the caller scales against. A non-positive peak gives
 *   `max: 0`, and the caller draws nothing rather than dividing by it.
 */
export const niceScale = (peak, rows = 4) => {
  if (!(peak > 0) || !Number.isFinite(peak)) return { max: 0, step: 0, ticks: [0] };

  const rough = peak / rows;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalised = rough / magnitude;
  const mantissa = MANTISSAS.find((candidate) => candidate >= normalised - 1e-9) ?? 10;
  const step = mantissa * magnitude;

  return {
    max: step * rows,
    step,
    ticks: Array.from({ length: rows + 1 }, (_, index) => index * step),
  };
};

/**
 * Round ticks covering a range that does **not** start at zero.
 *
 * A bar chart's baseline is zero — a bar is a length from nothing. A scatter's
 * is not: technicians billing between 262 and 455 plotted from zero would sit
 * in the top 40% of the plot, and the relationship the chart exists to show
 * would be squeezed out of it. So the axis is widened to round numbers
 * *around* the data instead of down to the origin.
 *
 * The tick count comes out of the rounding rather than being fixed, so the
 * caller passes `ticks.length - 1` to `ChartGrid` and the lines still match.
 *
 * @param {number} min
 * @param {number} max
 * @param {number} [rows=4] Target number of intervals, before rounding.
 * @returns {{ lo: number, hi: number, step: number, ticks: number[] }}
 */
export const niceRange = (min, max, rows = 4) => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { lo: 0, hi: 1, step: 1, ticks: [0, 1] };
  if (min === max) {
    // A single distinct value still needs an axis with two ends.
    const pad = Math.abs(min) || 1;
    return { lo: min - pad, hi: min + pad, step: pad, ticks: [min - pad, min, min + pad] };
  }

  const rough = (max - min) / rows;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalised = rough / magnitude;
  const mantissa = MANTISSAS.find((candidate) => candidate >= normalised - 1e-9) ?? 10;
  const step = mantissa * magnitude;

  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const count = Math.round((hi - lo) / step);

  return {
    lo,
    hi,
    step,
    // Rebuilt from `lo` rather than accumulated, so floating point does not
    // drift a tick into 349.99999999999994.
    ticks: Array.from({ length: count + 1 }, (_, index) => lo + index * step),
  };
};

export default niceScale;
